/**
 * AiSettingsCard — Anthropic credential + AI-layer configuration.
 *
 * The API key is WRITE-ONLY across the API boundary: it is stored encrypted (AES-256-GCM, the same
 * treatment broker credentials get) and never sent back to the browser. All this card ever receives
 * is `hasApiKey` and the last four characters, which is enough to prove a key is present without
 * exposing it.
 *
 * "Test connection" runs one real, minimal request so a bad key, a wrong model id or a broken
 * schema round-trip surfaces here rather than silently at run time.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Card, CardContent, CardHeader, Stack, Switch,
  FormControlLabel, Alert, Chip, MenuItem, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useAppTheme } from '../../hooks/useAppTheme';
import api from '../../services/api';
import aiService, { type AiModelCatalogue } from '../../services/ai.service';
import HelpLink from '../common/HelpLink';

interface AiConfig {
  enabled: boolean;
  hasApiKey: boolean;
  anthropicKeyLast4: string | null;
  anthropicWorkspaceId: string | null;
  models: { alert: string; wide: string; deep: string; intraday: string };
  effort: { alert: string; wide: string; deep: string; intraday: string };
  dailyCostCapUsd: number;
  bakeoffModel?: string | null;
  optionDeskSymbols?: string[] | null;
  shadowMode: boolean;
  assessAlerts: boolean;
  /** Engines the lane covers. Null or empty means all of them. */
  assessEngines?: string[] | null;
  promptVersion?: string;
  spentTodayUsd?: number;
  /** Family keyword capping automatic selection; null means uncapped. */
  modelTierCeiling?: string | null;
  resolvedModel?: { selected: string | null; reason?: string | null; tierNote?: string | null } | null;
}

/**
 * Tier ceilings for automatic selection. FAMILY keywords, never model ids.
 *
 * The whole point of automatic selection is that no model id is written down anywhere — a list of
 * ids here would go stale exactly the way the hardcoded `claude-opus-5` default did. The real
 * model list is fetched live from the account and rendered from `catalogue.eligible`.
 */
/**
 * The engines that actually produce entry signals reaching the assessment lane.
 *
 * Matched case-insensitively as a SUBSTRING of the engine's own strategy name, because a scan
 * writes its display name ("Bhairav_Scanner", "OM_Simple") rather than a slug. A list of exact
 * ids would silently stop matching the first time an engine was renamed, and the failure would be
 * invisible — signals would simply stop being assessed with no error anywhere.
 */
const ASSESSABLE_ENGINES = ['RockBand', 'Bhairav', 'Blast', 'Sniper', 'ShavedDoji', 'OM_Simple'];

const TIER_OPTIONS = [
  { id: '', label: 'No ceiling — always the best available' },
  { id: 'opus', label: 'Opus tier or below' },
  { id: 'sonnet', label: 'Sonnet tier or below' },
  { id: 'haiku', label: 'Haiku tier only — cheapest' },
];

const AiSettingsCard: React.FC = () => {
  const { C } = useAppTheme();
  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [keyInput, setKeyInput] = useState('');
  const [wsInput, setWsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalogue, setCatalogue] = useState<AiModelCatalogue | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/ai/config');
      const next = (r.data as any)?.data ?? null;
      setCfg(next);
      setWsInput(next?.anthropicWorkspaceId || '');
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not load AI settings');
    }
  }, []);

  /**
   * The account's reachable models. `refresh` re-consults the provider rather than the 24-hour
   * cache — used after a key change, and by the Refresh link, because a new key can reach a
   * different set of models than the old one.
   */
  const loadCatalogue = useCallback(async (refresh = false) => {
    try {
      setCatalogue(await aiService.getModels(refresh));
    } catch {
      setCatalogue(null);   // the card degrades to "list unavailable", never to a guessed list
    }
  }, []);

  useEffect(() => { load(); loadCatalogue(); }, [load, loadCatalogue]);

  const patch = async (body: Partial<AiConfig>) => {
    setSaving(true); setError(null);
    try {
      const r = await api.put('/ai/config', body);
      setCfg((r.data as any)?.data ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not save');
    } finally { setSaving(false); }
  };

  const saveKey = async () => {
    setSaving(true); setError(null); setTestResult(null);
    try {
      const r = await api.put('/ai/api-key', { apiKey: keyInput.trim() });
      setCfg((r.data as any)?.data ?? null);
      setKeyInput('');
      // A different key can reach a different set of models, so the cached resolution is stale
      // the moment the key changes.
      await loadCatalogue(true);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not save the key');
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null); setError(null);
    try {
      const r = await api.post('/ai/test-connection', {});
      setTestResult((r.data as any)?.data ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Test failed');
    } finally { setTesting(false); }
  };

  if (!cfg) {
    return (
      <Card><CardContent><Typography variant="body2" color="text.secondary">Loading AI settings…</Typography></CardContent></Card>
    );
  }

  return (
    <Card sx={{ border: `1px solid ${C.accentBorder}` }}>
      <CardHeader
        avatar={<PsychologyIcon sx={{ color: C.accent }} />}
        title="AI Decision Layer"
        subheader="Attaches a structured second opinion to your signals. It never places, sizes or stops a trade."
        action={
          <Chip
            size="small"
            label={cfg.enabled ? (cfg.shadowMode ? 'SHADOW' : 'LIVE') : 'OFF'}
            sx={{
              fontWeight: 700,
              backgroundColor: !cfg.enabled ? C.dim : cfg.shadowMode ? C.warn : C.bull,
              color: '#fff',
            }}
          />
        }
      />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Credential ─────────────────────────────────────────────── */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Anthropic API key</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Stored encrypted on the server and never sent back to this browser. Create one at{' '}
          <b>console.anthropic.com → API keys</b>.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
          <TextField
            size="small" fullWidth type="password" autoComplete="off"
            placeholder={cfg.hasApiKey ? `Saved — ends in ${cfg.anthropicKeyLast4}. Enter a new key to replace it.` : 'sk-ant-…'}
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
          />
          <Button variant="contained" disabled={saving || !keyInput.trim()} onClick={saveKey} sx={{ minWidth: 96 }}>
            Save
          </Button>
          {cfg.hasApiKey && (
            <Tooltip title="Remove the stored key. The AI layer will report itself unavailable until a new one is saved.">
              <span>
                <Button color="error" variant="outlined" disabled={saving}
                  onClick={async () => { setKeyInput(''); setSaving(true);
                    try { const r = await api.put('/ai/api-key', { apiKey: '' }); setCfg((r.data as any)?.data ?? null); }
                    finally { setSaving(false); } }}>
                  Clear
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            size="small" fullWidth label="Workspace ID (identity-linked keys only)"
            placeholder="leave blank unless Anthropic asks for it"
            value={wsInput} onChange={(e) => setWsInput(e.target.value)}
            helperText="Required if your key is identity-linked. console.anthropic.com → Settings → Workspaces."
          />
          <Button
            variant="outlined" sx={{ minWidth: 96, alignSelf: 'flex-start' }}
            disabled={saving || wsInput === (cfg.anthropicWorkspaceId || '')}
            onClick={() => patch({ anthropicWorkspaceId: wsInput.trim() || null } as any)}
          >
            Save
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Button size="small" variant="outlined" onClick={testConnection} disabled={testing || !cfg.hasApiKey}>
            {testing ? <CircularProgress size={16} /> : 'Test connection'}
          </Button>
          {testResult && (
            <Typography variant="caption" sx={{ color: testResult.ok ? C.bull : C.bear }}>
              {testResult.ok
                ? `OK — ${testResult.model}, ${testResult.latencyMs}ms, $${Number(testResult.costUsd).toFixed(5)}`
                : `Failed — ${testResult.status || 'error'}`}
            </Typography>
          )}
        </Stack>

        {testResult && !testResult.ok && (
          <Alert severity={testResult.hint ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {testResult.hint || 'The connection test failed.'}
            {testResult.reason && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.8, wordBreak: 'break-word' }}>
                Anthropic said: {String(testResult.reason).slice(0, 300)}
              </Typography>
            )}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── Behaviour ──────────────────────────────────────────────── */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={cfg.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />}
            label="Enable the AI layer"
          />
          {/* The master switch is the one control a reader has to be able to trust without reading
              the code, so it says WHY it is trustworthy rather than merely asserting that it is. */}
          <Typography variant="caption" color="text.secondary" sx={{ pl: 6, mt: -0.5 }}>
            The master switch. With it off, <strong>nothing can be spent</strong> — every billed path
            obtains its client from one function, and that function returns nothing when this is off,
            before any client is constructed. There is no second route to the model.
            {' '}<HelpLink to="ai-desk/cost" variant="inline" label="how the off switch is enforced" />
          </Typography>
          <FormControlLabel
            control={<Switch checked={cfg.assessAlerts} disabled={!cfg.enabled}
              onChange={(e) => patch({ assessAlerts: e.target.checked })} />}
            label="Assess incoming signals"
          />
          {/* Established from the call sites, not from the switch's name. Four things reach the
              assessment service and only two of them are gated here; saying which is the whole
              point of a label on a switch that spends money. */}
          <Typography variant="caption" color="text.secondary" sx={{ pl: 6, mt: -0.5 }}>
            Automatically assesses <strong>entry signals as they arrive</strong> — both TradingView
            webhook alerts and the in-app scan engines (RockBand, Bhairav, Blast, Sniper, Shaved
            Doji). Exits are never assessed. This does <strong>not</strong> affect the nightly board,
            an explicit &ldquo;Ask AI&rdquo; or a position review — those run regardless. Off means
            the verdict chips stay empty until you press Ask AI.
            {' '}<HelpLink to="ai-desk/cost" variant="inline" label="what this costs" />
          </Typography>
          {/* Naming engines makes "on" affordable. The switch above is all-or-nothing across every
              producer of entry signals, and at ~Rs 2.70 an assessment a busy scan cycle is real
              money — so turning it on was a bigger commitment than one switch should be able to
              make. Empty means every engine, which is what the switch has always meant. */}
          {cfg.assessAlerts && (
            <Box sx={{ pl: 6, pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Assess signals from{cfg.assessEngines?.length ? '' : ' every engine'}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                {ASSESSABLE_ENGINES.map(eng => {
                  const on = !!cfg.assessEngines?.some(e => e.toLowerCase() === eng.toLowerCase());
                  return (
                    <Chip key={eng} size="small" label={eng} disabled={!cfg.enabled}
                      variant={on ? 'filled' : 'outlined'}
                      color={on ? 'primary' : 'default'}
                      onClick={() => {
                        const cur = cfg.assessEngines || [];
                        const next = on
                          ? cur.filter(e => e.toLowerCase() !== eng.toLowerCase())
                          : [...cur, eng];
                        patch({ assessEngines: next.length ? next : null });
                      }} />
                  );
                })}
                {!!cfg.assessEngines?.length && (
                  <Chip size="small" label="All engines" variant="outlined" disabled={!cfg.enabled}
                    onClick={() => patch({ assessEngines: null })} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {cfg.assessEngines?.length
                  ? 'Signals from any other engine are not assessed and cost nothing.'
                  : 'Nothing selected means every engine, including the TradingView webhook.'}
                {' '}An explicit &ldquo;Ask AI&rdquo; is never filtered by this.
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={cfg.shadowMode} disabled={!cfg.enabled}
              onChange={(e) => patch({ shadowMode: e.target.checked })} />}
            label="Shadow mode"
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 6, mt: -0.5 }}>
            In shadow mode the AI records and scores itself but its verdicts are presented as
            &ldquo;under evaluation&rdquo; rather than as recommendations. Leave this on until the
            measured hit rate justifies acting on it.
          </Typography>
        </Stack>

        {/* ── Model + spend ────────────────────────────────────────────
            The model list is fetched from the account, never hardcoded. "Automatic" resolves to
            the strongest model this key can actually reach that also supports structured outputs —
            so a better model is adopted the day it ships, with no code change. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
          <TextField
            select size="small" fullWidth label="Model for signal assessment"
            value={cfg.models?.alert || 'auto'} disabled={!cfg.enabled}
            onChange={(e) => patch({ models: { ...cfg.models, alert: e.target.value } } as any)}
          >
            <MenuItem value="auto">
              Automatic — best available
              {catalogue?.selected ? ` (now: ${catalogue.selected})` : ''}
            </MenuItem>
            {/* Every capable model the account can reach, ceiling or not. A ceiling caps what
                AUTOMATIC selection reaches for; it does not take a model away from you. Picking
                one here pins it until you change it back. */}
            {(catalogue?.eligible || []).map(m => (
              <MenuItem key={m.id} value={m.id}>
                {m.displayName} (${m.price.inPerM} / ${m.price.outPerM} per 1M)
                {m.aboveCeiling && ' — above your automatic ceiling'}
              </MenuItem>
            ))}
            {/* A model this app cannot rank is still pinnable by hand — it is just never chosen
                automatically, because guessing where an unknown family sits is not something to
                do on a live account. */}
            {(catalogue?.rejected || []).filter(r => /pin/.test(r.reason)).map(r => (
              <MenuItem key={r.id} value={r.id}>{r.id} — unranked, pin manually</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Automatic ceiling"
            value={cfg.modelTierCeiling || ''} disabled={!cfg.enabled}
            onChange={(e) => patch({ modelTierCeiling: e.target.value || null } as any)}
            sx={{ minWidth: 220 }}
            helperText="Caps automatic picks only — you can still pin any model above it"
          >
            {TIER_OPTIONS.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
          </TextField>
          <TextField
            size="small" type="number" label="Daily spend cap (USD)"
            value={cfg.dailyCostCapUsd} disabled={!cfg.enabled}
            onChange={(e) => patch({ dailyCostCapUsd: Number(e.target.value) })}
            sx={{ minWidth: 180 }}
          />
        </Stack>

        {/* What automatic selection actually chose, and why. Without this the user cannot tell
            which model is running or what it costs — which is the fair objection to letting the
            app choose for them. */}
        {cfg.enabled && catalogue && (
          <Box sx={{ mb: 1.5 }}>
            {catalogue.available ? (
              <Typography variant="caption" sx={{ color: C.dim, display: 'block' }}>
                {catalogue.selected
                  ? <>Automatic currently resolves to <b>{catalogue.selected}</b>
                      {catalogue.eligible.find(e => e.id === catalogue.selected) &&
                        <> at ${catalogue.eligible.find(e => e.id === catalogue.selected)!.price.inPerM}
                          {' / $'}{catalogue.eligible.find(e => e.id === catalogue.selected)!.price.outPerM} per 1M tokens</>}
                      . {catalogue.selectedReason}</>
                  : <>No model this account can reach also supports structured outputs, which the
                      output contract depends on. Pin one manually or check the account.</>}
                {' '}
                <Button size="small" sx={{ py: 0, px: 0.5, minWidth: 0, fontSize: '0.7rem' }}
                  onClick={() => loadCatalogue(true)}>
                  Refresh
                </Button>
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: C.dim }}>
                Model list unavailable — {catalogue.reason}.
              </Typography>
            )}
            {catalogue.tierNote && (
              <Alert severity="warning" sx={{ mt: 0.8, py: 0.2 }}>
                <Typography variant="caption">{catalogue.tierNote}</Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Which underlyings get a nightly option recommendation. Same principle as the bake-off
            below: anything that spends money on a schedule must be switchable here. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }} alignItems="center">
          <TextField
            select size="small" fullWidth label="Nightly option recommendations"
            value={(cfg.optionDeskSymbols || []).join(',')} disabled={!cfg.enabled}
            onChange={(e) => patch({
              optionDeskSymbols: e.target.value ? e.target.value.split(',') : [],
            } as any)}
          >
            <MenuItem value="">Off — no nightly option run</MenuItem>
            <MenuItem value="BTC">BTC only</MenuItem>
            <MenuItem value="BTC,ETH">BTC and ETH</MenuItem>
          </TextField>
          <Tooltip arrow title="Reads the option chain across every live expiry, has the model choose a structure family and expiry window, prices concrete structures from the live chain, then ranks them. Runs as a batch at half price because nothing is waiting on it. Results appear on the AI Desk under Options.">
            <Typography variant="caption" sx={{ color: C.dim, minWidth: 210, cursor: 'help' }}>
              {(cfg.optionDeskSymbols || []).length
                ? `About $0.10 per underlying per night, batched.`
                : 'Off. Use "Ask the desk" on the AI Desk for an on-demand run.'}
            </Typography>
          </Tooltip>
        </Stack>

        {/* Bake-off. Its own row with the cost stated plainly, because switching it on doubles
            the nightly model spend and turning it off must never require asking anyone. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }} alignItems="center">
          <TextField
            select size="small" fullWidth label="Compare against (nightly bake-off)"
            value={cfg.bakeoffModel || ''} disabled={!cfg.enabled}
            onChange={(e) => patch({ bakeoffModel: e.target.value || null } as any)}
          >
            <MenuItem value="">Off — no comparison run</MenuItem>
            {/* Deliberately NOT offering "automatic" here. The challenger's whole job is to be a
                DIFFERENT model from the primary, and an automatic challenger would resolve to the
                same id and compare the model with itself. The primary is excluded for the same
                reason. */}
            {(catalogue?.eligible || [])
              .filter(m => m.id !== catalogue?.selected)
              .map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.displayName} (${m.price.inPerM} / ${m.price.outPerM} per 1M)
                </MenuItem>
              ))}
          </TextField>
          <Tooltip arrow title="A second model judges the SAME candidates from the SAME stored inputs, and both are scored against what the market actually did. It is the only way to find out whether the expensive model earns its cost on your data rather than assuming it. Results appear on the AI Desk once 30 candidates have both a verdict and a resolved outcome.">
            <Typography variant="caption" sx={{ color: C.dim, minWidth: 210, cursor: 'help' }}>
              {cfg.bakeoffModel
                ? 'Roughly doubles nightly spend (~$0.26/night). Turn off here at any time.'
                : 'Off. No second model runs and nothing extra is spent.'}
            </Typography>
          </Tooltip>
        </Stack>

        <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, backgroundColor: C.neutralMuted, border: `1px solid ${C.neutralBorder}` }}>
          <Typography variant="caption" color="text.secondary">
            Spent today: <b>${Number(cfg.spentTodayUsd || 0).toFixed(4)}</b> of ${Number(cfg.dailyCostCapUsd).toFixed(2)}
            {cfg.promptVersion ? ` · prompt ${cfg.promptVersion}` : ''}
            <br />
            Assessing your signals costs roughly a fraction of a cent each. Opus 5 is the default
            because the judgement quality is the whole point and the saving from a weaker model is
            a few dollars a month.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AiSettingsCard;
